/**
 * Utility functions for determining which dashboard learning banners to show.
 * These helpers handle mixed subscription structures (string, object, or array)
 * and cleanly separate business logic from component presentation.
 */

/**
 * Safely checks if any subscription matches an allowed list,
 * handling string, object, or array claim structures.
 */
export function checkSubscriptionMatch(subs: any, list: string[]): boolean {
  if (Array.isArray(subs)) {
    return subs.some((sub: any) => list.includes(typeof sub === 'string' ? sub : sub?.name || ''));
  } else if (typeof subs === 'string') {
    return list.includes(subs);
  }
  return false;
}

/**
 * Checks if the user has any Role-Based learning subscription.
 */
export function hasRoleBasedSubscription(subs: any): boolean {
  const roleBasedList = [
    'Genesys Cloud Learning Subscription',
    'Partner Premier Learning Subscription',
    'Genesys Cloud Premier Partner Learning Subscription',
    'AI Essentials Subscription',
    'AI Essentials Learning Subscription',
  ];
  return checkSubscriptionMatch(subs, roleBasedList);
}

/**
 * Checks if the user has any Core or Free learning subscription.
 */
export function hasCoreLearningSubscription(subs: any): boolean {
  const coreList = [
    'Genesys Cloud Core Learning Subscription',
    'Free Beyond Learning Subscription',
  ];
  return checkSubscriptionMatch(subs, coreList);
}

/**
 * Determines which dashboard banner to show.
 * If both exist, only show Role-Based (higher priority).
 */
export function getLearningBannerVisibility(subs: any): {
  showRoleBasedBox: boolean;
  showFreeLearningBox: boolean;
} {
  const hasRole = hasRoleBasedSubscription(subs);
  const hasCore = hasCoreLearningSubscription(subs);

  if (hasRole) {
    // Role-based subscriptions take precedence over core/free
    return { showRoleBasedBox: true, showFreeLearningBox: false };
  }

  if (hasCore) {
    return { showRoleBasedBox: false, showFreeLearningBox: true };
  }
  // Default: show neither
  return { showRoleBasedBox: false, showFreeLearningBox: true };
}
